import { useCallback, useEffect } from "react";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
  OnboardingAnalyticsEvent,
  OnboardingAnalyticsPageView,
} from "@/constants/analytics/Analytics";
import { OnboardingScreenPath } from "@/constants/Navigation";
import { OnboardingCalendarSyncTestID, TextTestID } from "@/constants/TestID";
import { useOnboarding } from "@/context-providers/OnboardingProvider";
import { router } from "@/crossPlatform/expo-router";
import { useAnalytics } from "@/services/Analytics";
import CalendarSyncStep from "@/ui/components/onboarding/CalendarSyncStep";
import { Button, FAIconName, IconButton, Text } from "@/ui/core";
import { HStack, ScreenContainer, VStack } from "@/ui/layout";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

const Screen = () => {
  const { track, trackPageView } = useAnalytics();
  const { shouldBypassConsentScreen } = useOnboarding();
  const baseTestID = OnboardingCalendarSyncTestID.Primary;

  useEffect(() => {
    trackPageView(OnboardingAnalyticsPageView.CalendarSync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = useCallback(() => {
    track(OnboardingAnalyticsEvent.NextButtonPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.Source]: OnboardingAnalyticsPageView.CalendarSync,
    });

    // After calendar sync, continue to consent/welcome
    const nextScreen = shouldBypassConsentScreen
      ? OnboardingScreenPath.Welcome
      : OnboardingScreenPath.Consent;

    requestAnimationFrame(() => {
      router.replace(nextScreen);
    });
  }, [track, shouldBypassConsentScreen]);

  return (
    <ScreenContainer
      backgroundColor={ColorToken.Primary10}
      isScrollView
      isStackLayout
    >
      <VStack
        flex={1}
        gap={24}
        justifyContent="space-between"
        paddingBottom={LayoutToken.OnboardingScreenHorizontalPadding}
        paddingHorizontal={LayoutToken.OnboardingScreenHorizontalPadding}
        paddingTop={LayoutToken.ModalHeaderPadding}
      >
        <VStack gap={24}>
          <VStack gap={12}>
            <Text
              color={ColorToken.Primary90Variant}
              testID={joinTestIDs(baseTestID, TextTestID.Title)}
              variant="h2"
            >
              Connect your calendar
            </Text>
            <Text
              color={ColorToken.Primary90Variant}
              testID={joinTestIDs(baseTestID, TextTestID.Description)}
              variant="body"
            >
              Automatically sync all of your Equip events - both provider
              sessions and group classes - to your personal calendar.
            </Text>
          </VStack>
          <CalendarSyncStep testID={joinTestIDs(baseTestID, "sync-card")} />
        </VStack>
        <VStack gap={12}>
          <HStack gap={24} justifyContent="flex-end">
            <IconButton
              iconName={FAIconName.ArrowLeft}
              onPress={() => router.replace(OnboardingScreenPath.Notifications)}
              shouldShowBorder
              testID={joinTestIDs(baseTestID, "back")}
            />
            <Button
              isFullWidth
              leftIcon={FAIconName.ArrowRight}
              onPress={handleContinue}
              testID={joinTestIDs(baseTestID, "continue")}
              title="Next"
            />
          </HStack>
        </VStack>
      </VStack>
    </ScreenContainer>
  );
};

export default Screen;
